import _extends from "@babel/runtime/helpers/extends";
import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';
import Text from '../../typography/Text';
import List from './components/List';
import Link from './components/Link';
import styles from './styles';
const useStyles = createUseStyles(styles, 'Markdown');
const Markdown = /*#__PURE__*/memo(_ref => {
  let {
    classes: classesProp,
    className: classNameProp,
    components,
    content,
    linkCallback,
    overrides: overridesProp
  } = _ref;
  const classes = useClasses(useStyles, classesProp);
  const override = getOverrides(overridesProp, Markdown.overrides);
  const rootProps = useMemo(() => ({
    children: content,
    components: {
      a: _ref2 => {
        let {
          node,
          children,
          ...props
        } = _ref2;
        return /*#__PURE__*/React.createElement(Link, _extends({
          variation: "primary",
          callback: linkCallback
        }, props, override.a), children);
      },
      p: _ref3 => {
        let {
          children
        } = _ref3;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "body",
          as: "p",
          className: classes.block
        }, override.p), children);
      },
      h1: _ref4 => {
        let {
          children
        } = _ref4;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "h5",
          as: "h1",
          className: classes.block
        }, override.h1), children);
      },
      h2: _ref5 => {
        let {
          children
        } = _ref5;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "h6",
          as: "h2",
          className: classes.block
        }, override.h2), children);
      },
      h3: _ref6 => {
        let {
          children
        } = _ref6;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "subtitleLg",
          medium: true,
          as: "h3",
          className: classes.block
        }, override.h3), children);
      },
      h4: _ref7 => {
        let {
          children
        } = _ref7;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "subtitle1",
          as: "h4",
          className: classes.block
        }, override.h4), children);
      },
      h5: _ref8 => {
        let {
          children
        } = _ref8;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "subtitle",
          medium: true,
          as: "h5",
          className: classes.block
        }, override.h5), children);
      },
      h6: _ref9 => {
        let {
          children
        } = _ref9;
        return /*#__PURE__*/React.createElement(Text, _extends({
          type: "body",
          medium: true,
          as: "h6",
          className: classes.block
        }, override.h6), children);
      },
      ul: _ref0 => {
        let {
          children
        } = _ref0;
        return /*#__PURE__*/React.createElement(List, _extends({
          items: children
        }, override.ul));
      },
      ol: _ref1 => {
        let {
          children
        } = _ref1;
        return /*#__PURE__*/React.createElement(List, _extends({
          items: children,
          type: "ordered"
        }, override.ol));
      },
      ...components
    },
    ...override.root
  }), [classes.block, components, content, linkCallback, override.a, override.h1, override.h2, override.h3, override.h4, override.h5, override.h6, override.ol, override.p, override.root, override.ul]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(classes.root, classNameProp)
  }, /*#__PURE__*/React.createElement(ReactMarkdown, rootProps));
});
Markdown.overrides = ['root', 'a', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol'];
process.env.NODE_ENV !== "production" ? Markdown.propTypes = {
  /** Custom CSS class for styling the Markdown container */
  className: PropTypes.string,
  /**
   * Custom components to override default markdown rendering.
   * Can be used to provide custom renderers for specific markdown elements.
   * Each key should be a valid markdown element tag (e.g., 'a', 'p', 'h1').
   */
  components: PropTypes.object,
  /**
   * Markdown content to render.
   * Raw markdown string that will be processed and converted to React components.
   */
  content: PropTypes.string.isRequired,
  /**
   * Callback function that is called when a link in the markdown content is clicked.
   * Receives an object with { event, attributes, link } where:
   * - event: The click event
   * - attributes: Parsed attributes from the link title (as JSON)
   * - link: Function to execute the default link behavior
   */
  linkCallback: PropTypes.func,
  /**
   * Object with custom style overrides for inner elements.
   * Supports overrides for 'root', 'a', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol'.
   */
  overrides: PropTypes.object
} : void 0;
export default Markdown;